import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import QRScanner from './components/QRScanner';
import VerificationResult from './components/VerificationResult';
import VerificationHistory from './components/VerificationHistory';
import IssuerRegistry from './components/IssuerRegistry';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const VerificationPortal = () => {
  const [activeTab, setActiveTab] = useState('scanner');
  const [isScanning, setIsScanning] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [verificationHistory, setVerificationHistory] = useState([]);
  const [issuers, setIssuers] = useState([]);

  // Mock data for verification history
  const mockHistory = [
    {
      id: 'vh_001',
      credentialId: 'vc_vax_001',
      credentialName: 'COVID-19 Vaccination Certificate',
      credentialType: 'vaccination_proof',
      subjectName: 'John Smith',
      issuerName: 'Department of Health',
      issuerDID: 'did:web:health.gov',
      status: 'valid',
      timestamp: '2024-09-22T09:15:00Z',
      zkProofs: [
        { type: 'age_verification', description: 'Age over 18 without revealing DOB' }
      ]
    },
    {
      id: 'vh_002',
      credentialId: 'vc_ins_002',
      credentialName: 'Health Insurance Coverage',
      credentialType: 'insurance_coverage',
      subjectName: 'Sarah Johnson',
      issuerName: 'BlueCross BlueShield',
      issuerDID: 'did:web:bcbs.com',
      status: 'expired',
      timestamp: '2024-09-22T08:30:00Z',
      zkProofs: []
    },
    {
      id: 'vh_003',
      credentialId: 'vc_train_003',
      credentialName: 'Safety Training Certificate',
      credentialType: 'training_certificate',
      subjectName: 'Michael Brown',
      issuerName: 'OSHA Training Institute',
      issuerDID: 'did:web:osha.gov',
      status: 'valid',
      timestamp: '2024-09-22T07:45:00Z',
      zkProofs: []
    },
    {
      id: 'vh_004',
      credentialId: 'vc_rx_004',
      credentialName: 'Prescription Authorization',
      credentialType: 'prescription_auth',
      subjectName: 'Emily Davis',
      issuerName: 'City Medical Center',
      issuerDID: 'did:web:citymedical.org',
      status: 'revoked',
      timestamp: '2024-09-21T16:20:00Z',
      zkProofs: []
    },
    {
      id: 'vh_005',
      credentialId: 'vc_vax_005',
      credentialName: 'Flu Vaccination Record',
      credentialType: 'vaccination_proof',
      subjectName: 'Robert Wilson',
      issuerName: 'Community Health Clinic',
      issuerDID: 'did:web:communityhealthclinic.org',
      status: 'valid',
      timestamp: '2024-09-21T14:10:00Z',
      zkProofs: [
        { type: 'vaccination_status', description: 'Vaccination validity without full record' }
      ]
    }
  ];

  // Mock data for issuer registry
  const mockIssuers = [
    {
      id: 'issuer_001',
      name: 'Department of Health',
      did: 'did:web:health.gov',
      domain: 'health.gov',
      status: 'verified',
      trustScore: 98,
      registrationDate: '2023-01-15T00:00:00Z',
      lastVerification: '2024-09-20T12:00:00Z',
      credentialsIssued: 15420,
      authorizedTypes: ['Vaccination Proof', 'Medical Certificate', 'Health Record'],
      smartContract: '0x1234...5678',
      network: 'Ethereum Mainnet'
    },
    {
      id: 'issuer_002',
      name: 'BlueCross BlueShield',
      did: 'did:web:bcbs.com',
      domain: 'bcbs.com',
      status: 'verified',
      trustScore: 95,
      registrationDate: '2023-03-20T00:00:00Z',
      lastVerification: '2024-09-19T15:30:00Z',
      credentialsIssued: 8750,
      authorizedTypes: ['Insurance Coverage', 'Membership Proof'],
      smartContract: '0x2345...6789',
      network: 'Ethereum Mainnet'
    },
    {
      id: 'issuer_003',
      name: 'OSHA Training Institute',
      did: 'did:web:osha.gov',
      domain: 'osha.gov',
      status: 'verified',
      trustScore: 97,
      registrationDate: '2023-02-10T00:00:00Z',
      lastVerification: '2024-09-21T10:15:00Z',
      credentialsIssued: 12300,
      authorizedTypes: ['Training Certificate', 'Safety Compliance', 'Certification'],
      smartContract: '0x3456...7890',
      network: 'Ethereum Mainnet'
    },
    {
      id: 'issuer_004',
      name: 'City Medical Center',
      did: 'did:web:citymedical.org',
      domain: 'citymedical.org',
      status: 'pending',
      trustScore: 85,
      registrationDate: '2024-08-15T00:00:00Z',
      lastVerification: '2024-09-15T09:00:00Z',
      credentialsIssued: 450,
      authorizedTypes: ['Prescription Auth', 'Medical Record', 'Lab Result'],
      smartContract: '0x4567...8901',
      network: 'Polygon'
    }
  ];

  useEffect(() => {
    setVerificationHistory(mockHistory);
    setIssuers(mockIssuers);
  }, []);

  const handleScanResult = (qrData) => {
    try {
      // Parse QR data and create verification result
      const credentialData = typeof qrData === 'string' ? JSON.parse(qrData) : qrData;
      
      // Mock verification result based on scanned data
      const mockResult = {
        credentialId: credentialData?.credentialId || 'vc_scan_001',
        credentialName: 'COVID-19 Vaccination Certificate',
        credentialType: credentialData?.type || 'vaccination_proof',
        subjectName: 'John Smith',
        issuerName: 'Department of Health',
        issuerDID: credentialData?.issuer || 'did:web:health.gov',
        status: 'valid',
        issuanceDate: credentialData?.issuanceDate || '2024-03-15T10:30:00Z',
        expirationDate: credentialData?.expirationDate || '2025-03-15T10:30:00Z',
        validationChecks: [
          { description: 'Digital signature verification', passed: true, details: 'Ed25519 signature valid' },
          { description: 'Issuer registry check', passed: true, details: 'Issuer verified in blockchain registry' },
          { description: 'Expiration date check', passed: true, details: 'Valid until March 15, 2025' },
          { description: 'Revocation status check', passed: true, details: 'Not revoked' },
          { description: 'Schema validation', passed: true, details: 'Conforms to W3C VC standard' }
        ],
        blockchainProof: {
          txHash: '0xabcd1234567890abcd1234567890abcd1234567890abcd1234567890abcd1234',
          blockNumber: 18456789,
          network: 'Ethereum Mainnet',
          timestamp: '2024-03-15T10:35:00Z'
        },
        zkProofs: [
          {
            type: 'age_verification',
            description: 'Age over 18 verification without revealing date of birth'
          },
          {
            type: 'vaccination_status',
            description: 'Vaccination validity proof without exposing full medical record'
          }
        ]
      };

      setVerificationResult(mockResult);
      setIsScanning(false);

      // Add to history
      const historyEntry = {
        id: `vh_${Date.now()}`,
        ...mockResult,
        timestamp: new Date()?.toISOString()
      };
      setVerificationHistory(prev => [historyEntry, ...prev]);

    } catch (error) {
      console.error('Error processing QR scan:', error);
      // Handle invalid QR data
      const errorResult = {
        credentialId: 'invalid',
        credentialName: 'Invalid Credential',
        credentialType: 'unknown',
        subjectName: 'Unknown',
        issuerName: 'Unknown',
        issuerDID: 'unknown',
        status: 'invalid',
        issuanceDate: new Date()?.toISOString(),
        expirationDate: null,
        validationChecks: [
          { description: 'QR code format validation', passed: false, details: 'Invalid QR code format' }
        ],
        blockchainProof: null,
        zkProofs: []
      };
      setVerificationResult(errorResult);
      setIsScanning(false);
    }
  };

  const handleToggleScanning = () => {
    setIsScanning(!isScanning);
    if (verificationResult) {
      setVerificationResult(null);
    }
  };

  const handleCloseResult = () => {
    setVerificationResult(null);
  };

  const handleSaveReport = () => {
    // Mock report generation
    const reportData = {
      verificationId: `VR_${Date.now()}`,
      timestamp: new Date()?.toISOString(),
      result: verificationResult
    };
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `verification_report_${reportData?.verificationId}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleViewHistoryDetails = (item) => {
    setVerificationResult(item);
    setActiveTab('scanner');
  };

  const handleExportHistory = () => {
    const blob = new Blob([JSON.stringify(verificationHistory, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `verification_history_${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleVerifyIssuer = (issuerId) => {
    // Mock issuer verification
    console.log('Verifying issuer:', issuerId);
    // In real implementation, this would trigger blockchain verification
  };

  const handleViewIssuerDetails = (issuer) => {
    // Mock issuer details view
    console.log('Viewing issuer details:', issuer);
    // In real implementation, this would open a detailed issuer view
  };

  const tabs = [
    { id: 'scanner', label: 'QR Scanner', icon: 'QrCode' },
    { id: 'history', label: 'History', icon: 'History' },
    { id: 'registry', label: 'Issuer Registry', icon: 'Building' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Verification Portal - HealthGuard Pro</title>
        <meta name="description" content="Verify credentials through QR code scanning and blockchain validation with zero-knowledge proof support." />
      </Helmet>
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Verification Portal</h1>
                <p className="text-muted-foreground">
                  Verify credentials through QR scanning and blockchain validation
                </p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="CheckCircle" size={20} className="text-success" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-foreground">
                      {verificationHistory?.filter(h => h?.status === 'valid')?.length}
                    </p>
                    <p className="text-sm text-muted-foreground">Valid Today</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Scan" size={20} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-foreground">{verificationHistory?.length}</p>
                    <p className="text-sm text-muted-foreground">Total Scans</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="Shield" size={20} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-foreground">
                      {verificationHistory?.filter(h => h?.zkProofs && h?.zkProofs?.length > 0)?.length}
                    </p>
                    <p className="text-sm text-muted-foreground">ZK Proofs</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Building" size={20} className="text-secondary" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-foreground">
                      {issuers?.filter(i => i?.status === 'verified')?.length}
                    </p>
                    <p className="text-sm text-muted-foreground">Trusted Issuers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-clinical ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-8">
            {activeTab === 'scanner' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <QRScanner
                  onScanResult={handleScanResult}
                  isScanning={isScanning}
                  onToggleScanning={handleToggleScanning}
                />
                {verificationResult && (
                  <VerificationResult
                    result={verificationResult}
                    onClose={handleCloseResult}
                    onSaveReport={handleSaveReport}
                  />
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <VerificationHistory
                history={verificationHistory}
                onViewDetails={handleViewHistoryDetails}
                onExportHistory={handleExportHistory}
              />
            )}

            {activeTab === 'registry' && (
              <IssuerRegistry
                issuers={issuers}
                onVerifyIssuer={handleVerifyIssuer}
                onViewIssuerDetails={handleViewIssuerDetails}
              />
            )}
          </div>

          {/* Help Section */}
          <div className="mt-12 bg-card rounded-lg border border-border p-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="HelpCircle" size={24} className="text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-foreground mb-2">How to Use the Verification Portal</h3>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <p>
                    <strong>QR Scanner:</strong> Use your camera to scan credential QR codes or enter codes manually. 
                    The system supports vaccination proofs, insurance coverage, training certificates, and prescription authorizations.
                  </p>
                  <p>
                    <strong>Zero-Knowledge Proofs:</strong> Some credentials support privacy-preserving verification, 
                    allowing you to verify specific attributes (like age requirements) without exposing sensitive personal information.
                  </p>
                  <p>
                    <strong>Blockchain Validation:</strong> All credentials are verified against blockchain registries 
                    to ensure authenticity and check revocation status in real-time.
                  </p>
                  <p>
                    <strong>Issuer Registry:</strong> View trusted credential issuers and their verification status. 
                    Only verified issuers can issue valid credentials in the system.
                  </p>
                </div>
                <div className="mt-4">
                  <Button variant="outline" size="sm">
                    <Icon name="ExternalLink" size={14} className="mr-2" />
                    View Full Documentation
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VerificationPortal;